import { extraModalDetails, planSections } from './buy/india/plans';
import type { PlanIncludeDetail, PerformanceLabPlan } from './buy/india/types';
import type { LearnMoreModalData, ModalSection } from './DiagnosticLearnMoreModal';

const ITEM_ID_TO_BUY_DETAIL_DESCRIPTION: Record<string, string> = {
  'system-wise-performance-score':
    'details.systemWisePerformanceScore.description',
  'action-plan': 'details.actionPlan.description',
  performanceScore: 'details.systemWisePerformanceScore.description',
  actionPlan: 'details.actionPlan.description',
  'biointelligence-ai': 'details.biointelligenceAi.description',
  'blood-vision': 'details.bloodVisionPeak.description',
  'body-composition-analysis': 'details.bodyComposition.description',
  'bone-density': 'details.boneDensity.description',
  electrocardiogram: 'details.ecg.description',
  vitals: 'details.vitals.description',
  vision: 'details.vision.description',
  hearing: 'details.hearing.description',
  'oral-health': 'details.oralHealth.description',
  cognition: 'details.cognition.description',
  'lung-health': 'details.lungHealth.description',
  'grip-strength': 'details.gripStrength.description',
  'vo2-max': 'details.vo2Max.description',
  'musculoskeletal-assessment': 'details.musculoskeletalBasic.description',
  'musculoskeletal-assessment-pro': 'details.musculoskeletalPro.description',
  nutridna: 'details.nutriDNA.description',
  'genomic-health-insights': 'details.genomicHealth.description',
  genetics: 'details.genomicHealth.description',
  nutrition: 'details.nutrition.description',
  'longevity-doctor-consultation': 'details.doctorConsultation.description',
  doctorConsult: 'details.doctorConsultation.description',
  consultation: 'details.doctorConsultation.description',
  'gut-microbiome': 'details.gutMicrobiome.description',
  'sleep-study-polysomnography': 'details.sleepStudy.description',
  'hyperbaric-oxygen-therapy-hbot': 'details.hbot.description',
  'infrared-sauna': 'details.infraredSauna.description',
  'cold-plunge': 'details.coldPlunge.description',
  cryotherapy: 'details.cryotherapy.description',
  decompression: 'details.decompression.description',
  'massage-guns': 'details.massageGuns.description',
  powerplate: 'details.powerplate.description',
  compression: 'details.compression.description',
  'localized-hot-cold-therapy': 'details.localizedHotCold.description',
  'sports-massage': 'details.sportsMassage.description',
  'iv-therapy':
    'sections.iv-therapy.subcategories.iv.plans.iv-miniboost.detail.description',
  'im-injections':
    'sections.iv-therapy.subcategories.im.plans.pl-im-b-family.detail.description',
};

const BUY_NOW_CTA_TEXT = 'Buy Now';

const isDetailedInclude = (
  include: string | { detail?: PlanIncludeDetail },
): include is { detail: PlanIncludeDetail } =>
  typeof include === 'object' && include != null && Boolean(include.detail);

const detailByDescriptionKey = (() => {
  const map = new Map<string, PlanIncludeDetail>();

  const collectDetail = (detail?: PlanIncludeDetail) => {
    if (!detail?.description) return;
    if (!map.has(detail.description)) {
      map.set(detail.description, detail);
    }
  };

  const collectPlanDetails = (plan: PerformanceLabPlan) => {
    collectDetail(plan.detail);
    plan.includes?.forEach((include) => {
      if (isDetailedInclude(include)) {
        collectDetail(include.detail);
      }
    });
  };

  planSections.forEach((section) => {
    section.plans.forEach(collectPlanDetails);
    section.subcategories?.forEach((subcategory) => {
      subcategory.plans.forEach(collectPlanDetails);
    });
  });

  extraModalDetails.forEach(collectDetail);

  return map;
})();

const toSections = (detail: PlanIncludeDetail): ModalSection[] => {
  if (detail.sections?.length) {
    return detail.sections.map((section, index) => ({
      title: section.title,
      content: section.content,
      defaultOpen: index === 0,
    }));
  }

  return [];
};

export const getBuyPrioritizedModalData = (
  itemId: string,
): LearnMoreModalData | undefined => {
  const mappedDescription = ITEM_ID_TO_BUY_DETAIL_DESCRIPTION[itemId];
  const buyDetail = mappedDescription
    ? detailByDescriptionKey.get(mappedDescription)
    : undefined;

  if (!buyDetail) return undefined;

  return {
    thumbnailSrc: buyDetail.thumbnailUrl,
    sectionImageSrc: buyDetail.imageUrl,
    sections: toSections(buyDetail),
    description: buyDetail.description,
    benefits: buyDetail.benefits,
    ctaText: BUY_NOW_CTA_TEXT,
  };
};
